import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plane, Hotel, User, Mail, Phone, CreditCard, 
  ChevronRight, Check, Loader2, ArrowLeft,
  Luggage, UtensilsCrossed, Armchair, Shield,
  Calendar, MapPin, Clock, Users
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Booking steps configuration
const FLIGHT_STEPS = [
  { id: 1, name: 'Flight Selected', icon: Plane },
  { id: 2, name: 'Passenger Details', icon: User },
  { id: 3, name: 'Seat Selection', icon: Armchair },
  { id: 4, name: 'Add-ons', icon: Luggage },
  { id: 5, name: 'Payment', icon: CreditCard },
  { id: 6, name: 'Confirmation', icon: Check }
];

const HOTEL_STEPS = [
  { id: 1, name: 'Hotel Selected', icon: Hotel },
  { id: 2, name: 'Guest Details', icon: User },
  { id: 3, name: 'Add-ons', icon: UtensilsCrossed },
  { id: 4, name: 'Payment', icon: CreditCard },
  { id: 5, name: 'Confirmation', icon: Check }
];

const BookingPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState(2);
  
  // Form states
  const [passengers, setPassengers] = useState([{
    title: 'Mr',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dob: ''
  }]);
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('card');

  // Get booking data from location state or fetch
  useEffect(() => {
    const initBooking = async () => {
      if (location.state?.booking) {
        setBooking(location.state.booking);
        setLoading(false);
      } else if (bookingId) {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_URL}/api/booking/${bookingId}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          const data = await response.json();
          if (data.success) {
            setBooking(data.booking);
          } else {
            toast.error('Booking not found');
            navigate('/travel');
          }
        } catch (error) {
          toast.error('Failed to load booking');
          navigate('/travel');
        }
      }
      setLoading(false);
    };
    
    initBooking();
  }, [bookingId, location.state, navigate]);

  const steps = booking?.type === 'flight' ? FLIGHT_STEPS : HOTEL_STEPS;

  const handlePassengerChange = (index, field, value) => {
    const updated = [...passengers];
    updated[index][field] = value;
    setPassengers(updated);
  };

  const addPassenger = () => {
    setPassengers([...passengers, {
      title: 'Mr',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dob: ''
    }]);
  };

  const removePassenger = (index) => {
    if (passengers.length > 1) {
      setPassengers(passengers.filter((_, i) => i !== index));
    }
  };

  const submitPassengers = async () => {
    if (!passengers[0].firstName || !passengers[0].lastName || !contactEmail || !contactPhone) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/booking/${booking.booking_id}/passengers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          booking_id: booking.booking_id,
          passengers: passengers,
          contact_email: contactEmail,
          contact_phone: contactPhone
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setBooking(data.booking);
        setCurrentStep(booking.type === 'flight' ? 3 : 3);
        toast.success('Details saved!');
      }
    } catch (error) {
      toast.error('Failed to save details');
    }
    setProcessing(false);
  };

  const submitSeats = async () => {
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/booking/${booking.booking_id}/seats`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ seats: selectedSeats })
      });
      
      const data = await response.json();
      if (data.success) {
        setBooking(data.booking);
        setCurrentStep(4);
        toast.success('Seats selected!');
      }
    } catch (error) {
      toast.error('Failed to save seats');
    }
    setProcessing(false);
  };

  const submitAddons = async () => {
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/booking/${booking.booking_id}/addons`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ addons: selectedAddons })
      });
      
      const data = await response.json();
      if (data.success) {
        setBooking(data.booking);
        setCurrentStep(booking.type === 'flight' ? 5 : 4);
        toast.success('Add-ons updated!');
      }
    } catch (error) {
      toast.error('Failed to save add-ons');
    }
    setProcessing(false);
  };

  const processPayment = async () => {
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/booking/${booking.booking_id}/payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          booking_id: booking.booking_id,
          payment_method: paymentMethod
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setBooking(data.booking);
        setCurrentStep(booking.type === 'flight' ? 6 : 5);
        toast.success('Payment successful! Booking confirmed.');
      }
    } catch (error) {
      toast.error('Payment failed');
    }
    setProcessing(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex flex-col items-center justify-center">
        <p className="text-gray-400">Booking not found</p>
        <button 
          onClick={() => navigate('/travel')}
          className="mt-4 px-4 py-2 bg-indigo-600 rounded-lg"
        >
          Go to Travel
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">Complete Your Booking</h1>
            <p className="text-gray-400">
              {booking.type === 'flight' ? 'Flight' : 'Hotel'} - {booking.booking_id}
            </p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="mb-8 overflow-x-auto">
          <div className="flex items-center min-w-max gap-2">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = step.id === currentStep;
              const isCompleted = step.id < currentStep;
              
              return (
                <React.Fragment key={step.id}>
                  <motion.div
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                      isActive 
                        ? 'bg-indigo-600 text-white' 
                        : isCompleted 
                          ? 'bg-green-600/20 text-green-400'
                          : 'bg-white/5 text-gray-500'
                    }`}
                    animate={{ scale: isActive ? 1.05 : 1 }}
                  >
                    {isCompleted ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                    <span className="text-sm font-medium whitespace-nowrap">{step.name}</span>
                  </motion.div>
                  {index < steps.length - 1 && (
                    <ChevronRight className="w-4 h-4 text-gray-600" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <AnimatePresence mode="wait">
              {/* Step 2: Passenger/Guest Details */}
              {currentStep === 2 && (
                <motion.div
                  key="passengers"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl p-6"
                >
                  <h2 className="text-xl font-semibold mb-6">
                    {booking.type === 'flight' ? 'Passenger Details' : 'Guest Details'}
                  </h2>
                  
                  {passengers.map((pax, index) => (
                    <div key={index} className="mb-6 p-4 bg-white/5 rounded-xl">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="font-medium">
                          {booking.type === 'flight' ? 'Passenger' : 'Guest'} {index + 1}
                        </h3>
                        {index > 0 && (
                          <button 
                            onClick={() => removePassenger(index)}
                            className="text-red-400 text-sm hover:text-red-300"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <select
                          value={pax.title}
                          onChange={(e) => handlePassengerChange(index, 'title', e.target.value)}
                          className="px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-indigo-500"
                          data-testid={`passenger-${index}-title`}
                        >
                          <option value="Mr">Mr</option>
                          <option value="Mrs">Mrs</option>
                          <option value="Ms">Ms</option>
                          <option value="Master">Master</option>
                          <option value="Miss">Miss</option>
                        </select>
                        
                        <input
                          type="text"
                          placeholder="First Name *"
                          value={pax.firstName}
                          onChange={(e) => handlePassengerChange(index, 'firstName', e.target.value)}
                          className="px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-indigo-500"
                          data-testid={`passenger-${index}-firstname`}
                        />
                        
                        <input
                          type="text"
                          placeholder="Last Name *"
                          value={pax.lastName}
                          onChange={(e) => handlePassengerChange(index, 'lastName', e.target.value)}
                          className="px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-indigo-500"
                          data-testid={`passenger-${index}-lastname`}
                        />
                      </div>
                    </div>
                  ))}
                  
                  <button
                    onClick={addPassenger}
                    className="mb-6 text-indigo-400 hover:text-indigo-300 text-sm"
                  >
                    + Add Another {booking.type === 'flight' ? 'Passenger' : 'Guest'}
                  </button>
                  
                  <div className="border-t border-white/10 pt-6">
                    <h3 className="font-medium mb-4">Contact Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                        <input
                          type="email"
                          placeholder="Email *"
                          value={contactEmail}
                          onChange={(e) => setContactEmail(e.target.value)}
                          className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-indigo-500"
                          data-testid="contact-email"
                        />
                      </div>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                        <input
                          type="tel"
                          placeholder="Phone *"
                          value={contactPhone}
                          onChange={(e) => setContactPhone(e.target.value)}
                          className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-indigo-500"
                          data-testid="contact-phone"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={submitPassengers}
                    disabled={processing}
                    className="mt-6 w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="submit-passengers-btn"
                  >
                    {processing ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        Continue <ChevronRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </motion.div>
              )}

              {/* Step 3: Seat Selection (Flights only) */}
              {currentStep === 3 && booking.type === 'flight' && (
                <motion.div
                  key="seats"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl p-6"
                >
                  <h2 className="text-xl font-semibold mb-6">Select Your Seats</h2>
                  
                  <div className="text-center py-12 text-gray-400">
                    <Armchair className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Seat selection will be available at check-in</p>
                    <p className="text-sm mt-2">You can skip this step for now</p>
                  </div>
                  
                  <div className="flex gap-4">
                    <button
                      onClick={() => setCurrentStep(4)}
                      className="flex-1 py-4 bg-white/10 rounded-xl font-semibold hover:bg-white/20 transition-colors"
                    >
                      Skip
                    </button>
                    <button
                      onClick={submitSeats}
                      disabled={processing}
                      className="flex-1 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {processing ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          Continue <ChevronRight className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Step 3/4: Add-ons */}
              {((currentStep === 4 && booking.type === 'flight') || (currentStep === 3 && booking.type === 'hotel')) && (
                <motion.div
                  key="addons"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl p-6"
                >
                  <h2 className="text-xl font-semibold mb-6">Add-ons & Extras</h2>
                  
                  <div className="space-y-4">
                    {booking.type === 'flight' ? (
                      <>
                        <label className="flex items-center justify-between p-4 bg-white/5 rounded-xl cursor-pointer hover:bg-white/10 transition-colors">
                          <div className="flex items-center gap-4">
                            <Luggage className="w-6 h-6 text-indigo-400" />
                            <div>
                              <p className="font-medium">Extra Baggage (15kg)</p>
                              <p className="text-sm text-gray-400">Add more luggage allowance</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-indigo-400">₹1,500</span>
                            <input type="checkbox" className="w-5 h-5 rounded" />
                          </div>
                        </label>
                        
                        <label className="flex items-center justify-between p-4 bg-white/5 rounded-xl cursor-pointer hover:bg-white/10 transition-colors">
                          <div className="flex items-center gap-4">
                            <UtensilsCrossed className="w-6 h-6 text-indigo-400" />
                            <div>
                              <p className="font-medium">Meal Selection</p>
                              <p className="text-sm text-gray-400">Pre-book your in-flight meal</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-indigo-400">₹500</span>
                            <input type="checkbox" className="w-5 h-5 rounded" />
                          </div>
                        </label>
                        
                        <label className="flex items-center justify-between p-4 bg-white/5 rounded-xl cursor-pointer hover:bg-white/10 transition-colors">
                          <div className="flex items-center gap-4">
                            <Shield className="w-6 h-6 text-indigo-400" />
                            <div>
                              <p className="font-medium">Travel Insurance</p>
                              <p className="text-sm text-gray-400">Coverage up to ₹5,00,000</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-indigo-400">₹299</span>
                            <input type="checkbox" className="w-5 h-5 rounded" />
                          </div>
                        </label>
                      </>
                    ) : (
                      <>
                        <label className="flex items-center justify-between p-4 bg-white/5 rounded-xl cursor-pointer hover:bg-white/10 transition-colors">
                          <div className="flex items-center gap-4">
                            <UtensilsCrossed className="w-6 h-6 text-indigo-400" />
                            <div>
                              <p className="font-medium">Breakfast Included</p>
                              <p className="text-sm text-gray-400">Daily breakfast buffet</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-indigo-400">₹800/night</span>
                            <input type="checkbox" className="w-5 h-5 rounded" />
                          </div>
                        </label>
                        
                        <label className="flex items-center justify-between p-4 bg-white/5 rounded-xl cursor-pointer hover:bg-white/10 transition-colors">
                          <div className="flex items-center gap-4">
                            <MapPin className="w-6 h-6 text-indigo-400" />
                            <div>
                              <p className="font-medium">Airport Transfer</p>
                              <p className="text-sm text-gray-400">Pick-up and drop service</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-indigo-400">₹1,200</span>
                            <input type="checkbox" className="w-5 h-5 rounded" />
                          </div>
                        </label>
                      </>
                    )}
                  </div>
                  
                  <div className="flex gap-4 mt-6">
                    <button
                      onClick={() => setCurrentStep(booking.type === 'flight' ? 5 : 4)}
                      className="flex-1 py-4 bg-white/10 rounded-xl font-semibold hover:bg-white/20 transition-colors"
                    >
                      Skip Add-ons
                    </button>
                    <button
                      onClick={submitAddons}
                      disabled={processing}
                      className="flex-1 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {processing ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          Continue <ChevronRight className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Step 5/4: Payment */}
              {((currentStep === 5 && booking.type === 'flight') || (currentStep === 4 && booking.type === 'hotel')) && (
                <motion.div
                  key="payment"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl p-6"
                >
                  <h2 className="text-xl font-semibold mb-6">Payment</h2>
                  
                  <div className="space-y-4 mb-6">
                    <label className={`flex items-center justify-between p-4 rounded-xl cursor-pointer transition-colors ${paymentMethod === 'card' ? 'bg-indigo-600/20 border-2 border-indigo-500' : 'bg-white/5 border-2 border-transparent'}`}>
                      <div className="flex items-center gap-4">
                        <CreditCard className="w-6 h-6" />
                        <span>Credit/Debit Card</span>
                      </div>
                      <input 
                        type="radio" 
                        name="payment" 
                        checked={paymentMethod === 'card'}
                        onChange={() => setPaymentMethod('card')}
                        className="w-5 h-5" 
                      />
                    </label>
                    
                    <label className={`flex items-center justify-between p-4 rounded-xl cursor-pointer transition-colors ${paymentMethod === 'upi' ? 'bg-indigo-600/20 border-2 border-indigo-500' : 'bg-white/5 border-2 border-transparent'}`}>
                      <div className="flex items-center gap-4">
                        <span className="text-xl">📱</span>
                        <span>UPI Payment</span>
                      </div>
                      <input 
                        type="radio" 
                        name="payment"
                        checked={paymentMethod === 'upi'}
                        onChange={() => setPaymentMethod('upi')}
                        className="w-5 h-5" 
                      />
                    </label>
                    
                    <label className={`flex items-center justify-between p-4 rounded-xl cursor-pointer transition-colors ${paymentMethod === 'netbanking' ? 'bg-indigo-600/20 border-2 border-indigo-500' : 'bg-white/5 border-2 border-transparent'}`}>
                      <div className="flex items-center gap-4">
                        <span className="text-xl">🏦</span>
                        <span>Net Banking</span>
                      </div>
                      <input 
                        type="radio" 
                        name="payment"
                        checked={paymentMethod === 'netbanking'}
                        onChange={() => setPaymentMethod('netbanking')}
                        className="w-5 h-5" 
                      />
                    </label>
                  </div>
                  
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6">
                    <p className="text-yellow-400 text-sm">
                      🔒 This is a demo. No real payment will be processed.
                    </p>
                  </div>
                  
                  <button
                    onClick={processPayment}
                    disabled={processing}
                    className="w-full py-4 bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {processing ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        Pay ₹{booking.total_amount?.toLocaleString()} <ChevronRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </motion.div>
              )}

              {/* Step 6/5: Confirmation */}
              {((currentStep === 6 && booking.type === 'flight') || (currentStep === 5 && booking.type === 'hotel')) && (
                <motion.div
                  key="confirmation"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl p-6 text-center"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', delay: 0.2 }}
                    className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
                  >
                    <Check className="w-10 h-10 text-green-500" />
                  </motion.div>
                  
                  <h2 className="text-2xl font-bold mb-2">Booking Confirmed!</h2>
                  <p className="text-gray-400 mb-6">
                    Your booking reference: <span className="text-indigo-400 font-mono">{booking.confirmation_number || booking.booking_id}</span>
                  </p>
                  
                  <div className="bg-white/5 rounded-xl p-4 mb-6 text-left">
                    <h3 className="font-medium mb-3">Booking Details</h3>
                    <div className="space-y-2 text-sm">
                      <p className="flex justify-between">
                        <span className="text-gray-400">Type:</span>
                        <span>{booking.type === 'flight' ? 'Flight' : 'Hotel'}</span>
                      </p>
                      <p className="flex justify-between">
                        <span className="text-gray-400">Amount Paid:</span>
                        <span className="text-green-400">₹{booking.total_amount?.toLocaleString()}</span>
                      </p>
                      <p className="flex justify-between">
                        <span className="text-gray-400">Status:</span>
                        <span className="text-green-400">Confirmed</span>
                      </p>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-400 mb-6">
                    Confirmation details have been sent to {booking.contact_info?.email}
                  </p>
                  
                  <div className="flex gap-4">
                    <button
                      onClick={() => navigate('/travel')}
                      className="flex-1 py-3 bg-white/10 rounded-xl font-semibold hover:bg-white/20 transition-colors"
                    >
                      Back to Travel
                    </button>
                    <button
                      onClick={() => navigate('/profile')}
                      className="flex-1 py-3 bg-indigo-600 rounded-xl font-semibold hover:bg-indigo-700 transition-colors"
                    >
                      View My Bookings
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Booking Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl p-6 sticky top-6">
              <h3 className="font-semibold mb-4">Booking Summary</h3>
              
              {booking.type === 'flight' && booking.flight_data && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Plane className="w-5 h-5 text-indigo-400" />
                    <div>
                      <p className="font-medium">{booking.flight_data.airline}</p>
                      <p className="text-sm text-gray-400">{booking.flight_data.flight_number}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-gray-500" />
                    <span className="text-sm">{booking.search_params?.departure_date}</span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-gray-500" />
                    <span className="text-sm">
                      {booking.search_params?.from_city} → {booking.search_params?.to_city}
                    </span>
                  </div>
                </div>
              )}
              
              {booking.type === 'hotel' && booking.hotel_data && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Hotel className="w-5 h-5 text-indigo-400" />
                    <div>
                      <p className="font-medium">{booking.hotel_data.name}</p>
                      <p className="text-sm text-gray-400">{booking.hotel_data.room_type}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-gray-500" />
                    <span className="text-sm">
                      {booking.search_params?.checkin_date} - {booking.search_params?.checkout_date}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-gray-500" />
                    <span className="text-sm">{booking.search_params?.city}</span>
                  </div>
                </div>
              )}
              
              <div className="border-t border-white/10 mt-6 pt-6">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-400">Base Price</span>
                  <span>₹{booking.total_amount?.toLocaleString()}</span>
                </div>
                {booking.addon_total > 0 && (
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-400">Add-ons</span>
                    <span>₹{booking.addon_total?.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg mt-4 pt-4 border-t border-white/10">
                  <span>Total</span>
                  <span className="text-indigo-400">₹{booking.total_amount?.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
